import { HTMLProps, ReactNode, useMemo } from 'react';
import { css, Theme } from '@emotion/react';
import { XCircle } from '@phosphor-icons/react';
import { Button } from 'client/components/Button';
import { CloseAnimation } from 'client/components/Other/CloseAnimation';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';
import { Pipeline } from './Pipeline';

export interface PostcardProps extends HTMLProps<HTMLDivElement> {
  text: string;
  button: ReactNode;
  open: boolean;
  onClose?: () => void;
}

export const Postcard = ({ text, button, open, onClose, ...other }: PostcardProps) => {
  const lines = useMemo(() => text.split('\n').map((line) => <span key={line}>{line}</span>), [text]);

  return (
    <CloseAnimation open={open} onClose={onClose}>
      {({ ref, onAnimateClose }) => (
        <article css={postcardStyle} ref={ref} {...other}>
          {/* Background graphic */}
          <Pipeline color="black" css={pipelineStyle} />

          {/* Close button */}
          {!!onClose && <Button icon={<XCircle size={24} weight="fill" />} onClick={onAnimateClose} css={closeStyle} />}

          {/* Text & call to action button */}
          <Typography variant="h5">{lines}</Typography>
          {button}
        </article>
      )}
    </CloseAnimation>
  );
};

const postcardStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 16px;
  padding: 24px;
  background-color: ${theme.color.primary[100]};
  border-radius: ${borderRadius}px;
  user-select: none;
  overflow: hidden;

  h5 {
    align-self: stretch;
    margin-bottom: 16px;
    line-height: 1.45;

    > span {
      display: block;
      color: ${theme.color.white};
    }
  }

  > a {
    text-decoration: none;
  }
`;

const pipelineStyle = css`
  position: absolute;
  width: 200%;
  left: -55%;
  opacity: 0.2;
`;

const closeStyle = (theme: Theme) => css`
  position: absolute;
  margin: -8px -8px 0 0;
  background: transparent;
  opacity: 0.6;

  svg {
    color: ${theme.color.white};
  }

  &:hover {
    background: transparent;
    box-shadow: none;
    opacity: 1;
  }
`;
